/**
 * The Chronicle's pure model (1.O-2 deliverable 1): the search, the merged pages, the grouping by
 * each row's lower bound (ruling g), every date, relative, range, NOW, dimming, status and
 * violation text. Month, era and season names come from the compiled calendar through 1.K-1's
 * formatters, so the GM's own calendar changes no code here. Titles are written as the GM wrote
 * them. Nothing here sorts: the server's order (rank, then id) is the Chronicle's order.
 */
import {
  type CompiledCalendar,
  formatTick,
  formatTimePoint,
  toParts,
  toTicks,
} from '@codex/calendar';
import {
  CHRONICLE_DENSITIES,
  type ChronicleDensity,
  type ChronicleRowV1,
  type ChronViolationV1,
  type ConstraintV1,
  type EffectiveStatus,
  type Flags,
  type PlacementV1,
  PRECISIONS,
  type Precision,
  type TickResultV1,
  type ViolationKind,
} from '@codex/contracts';
import { inboxVisible } from '../shell/destinations.js';

export interface ChronicleSearch {
  readonly density: ChronicleDensity;
}

/** `?density=`: kept when the contract accepts it, else `year`. */
export function parseChronicleSearch(raw: Record<string, unknown>): ChronicleSearch {
  const density = raw.density;
  return {
    density: CHRONICLE_DENSITIES.includes(density as ChronicleDensity)
      ? (density as ChronicleDensity)
      : 'year',
  };
}

/** The pages' rows in server order, a placement listed twice (a page boundary moved) kept once. */
export function mergePages(pages: readonly { readonly items: readonly ChronicleRowV1[] }[]) {
  const seen = new Set<string>();
  const merged: ChronicleRowV1[] = [];
  for (const page of pages) {
    for (const row of page.items) {
      if (seen.has(row.placement.id)) continue;
      seen.add(row.placement.id);
      merged.push(row);
    }
  }
  return merged;
}

/**
 * Per row, the lower bound it is grouped by: its own `boundLo`; for a row bounded only from above,
 * the `boundLo` of the first following row that has one (baseline section 6, rule 6); null for a
 * row with neither bound.
 */
export function rowLo(rows: readonly ChronicleRowV1[]): (number | null)[] {
  const los: (number | null)[] = new Array(rows.length).fill(null);
  let next: number | null = null;
  for (let i = rows.length - 1; i >= 0; i--) {
    const p = (rows[i] as ChronicleRowV1).placement;
    if (p.boundLo !== null) {
      los[i] = p.boundLo;
      next = p.boundLo;
    } else if (p.boundHi === null) {
      los[i] = null;
    } else {
      los[i] = next;
    }
  }
  return los;
}

export const UNDATED_HEADING = 'Ordered, not dated';
export const OUTSIDE_ERAS_HEADING = "Outside the calendar's eras";

export interface ChronicleGroup {
  readonly key: string;
  readonly heading: string;
  readonly lo: number | null;
  readonly hi: number | null;
  readonly dated: boolean;
  /** The index of the group's first row in the list. */
  readonly start: number;
  readonly rows: readonly ChronicleRowV1[];
}

function eraName(calendar: CompiledCalendar, key: string): string | null {
  const era = calendar.eras.find((e) => e.key === key);
  return era === undefined ? null : era.name || era.suffix || null;
}

function groupHead(
  calendar: CompiledCalendar,
  lo: number,
  density: ChronicleDensity,
): { parts: string; heading: string; lo: number | null; hi: number | null } {
  const parts = toParts(calendar, lo, density);
  if (parts === null) return { parts: 'none', heading: OUTSIDE_ERAS_HEADING, lo: null, hi: null };
  const ticks = toTicks(calendar, {
    loParts: parts,
    hiParts: null,
    precision: density,
    qualifier: 'exact',
  });
  const heading =
    density === 'era' ? eraName(calendar, String(parts.era)) : formatTick(calendar, lo, density);
  return {
    parts: JSON.stringify(parts),
    heading: heading ?? OUTSIDE_ERAS_HEADING,
    lo: ticks.status === 'ok' ? ticks.lo : null,
    hi: ticks.status === 'ok' ? ticks.hi : null,
  };
}

/**
 * Consecutive rows whose lower bounds share their parts at `density` form one group, headed by
 * that period ("1041 AL", "Ember 1042 AL", or the era's name); each run of rows with no lower
 * bound is a group "Ordered, not dated" where it falls. A key is unique even when a heading
 * repeats, since it carries the group's position.
 */
export function groupRows(
  rows: readonly ChronicleRowV1[],
  calendar: CompiledCalendar,
  density: ChronicleDensity,
): ChronicleGroup[] {
  const los = rowLo(rows);
  const groups: ChronicleGroup[] = [];
  let current: { id: string; group: ChronicleGroup; rows: ChronicleRowV1[] } | null = null;
  rows.forEach((row, index) => {
    const lo = los[index] ?? null;
    const head =
      lo === null
        ? { parts: 'undated', heading: UNDATED_HEADING, lo: null, hi: null }
        : groupHead(calendar, lo, density);
    if (current === null || current.id !== head.parts) {
      const list: ChronicleRowV1[] = [];
      const group: ChronicleGroup = {
        key: `${groups.length}:${head.parts}`,
        heading: head.heading,
        lo: head.lo,
        hi: head.hi,
        dated: lo !== null,
        start: index,
        rows: list,
      };
      groups.push(group);
      current = { id: head.parts, group, rows: list };
    }
    current.rows.push(row);
  });
  return groups;
}

export const INVALID_DATE_TEXT = "Can't be placed on the calendar";

/** The authored date of a placement: "c. 312 BF", "9 Ember 1042 – 10 Ember 1042", "… – ongoing". */
export function dateText(placement: PlacementV1, calendar: CompiledCalendar | null): string | null {
  const start = placement.start;
  if (start === null) return null;
  if (start.ticks.status === 'invalid' || placement.end?.ticks.status === 'invalid') {
    return INVALID_DATE_TEXT;
  }
  const written = (point: NonNullable<PlacementV1['start']>) =>
    (calendar === null ? null : formatTimePoint(calendar, point.point)) ||
    point.point.label ||
    point.point.rawInput;
  const from = written(start);
  if (placement.ongoing) return `${from} – ongoing`;
  return placement.end === null ? from : `${from} – ${written(placement.end)}`;
}

/** The title a row goes by: its placement's label, else its document's title. */
export function rowTitle(row: ChronicleRowV1): string {
  return row.placement.label ?? row.document.title;
}

export const OTHER_ENTRY = 'another entry';

function lagText(c: ConstraintV1): string {
  const unit = c.lagUnit;
  if (unit === null || (c.minLagN === null && c.maxLagN === null)) return '';
  const word = (n: number) => (n === 1 ? unit : `${unit}s`);
  const { minLagN: min, maxLagN: max } = c;
  if (min !== null && max !== null) {
    return min === max ? `${min} ${word(min)} ` : `${min}–${max} ${word(max)} `;
  }
  if (min !== null) return `at least ${min} ${word(min)} `;
  return `within ${max} ${word(max ?? 0)} `;
}

/**
 * An undated ranked row's place in words, from its own side of each constraint: "3 days after The
 * Siege of Vesk", "before The Coronation", "within …", "contains …", "at the same time as …",
 * joined with commas; a side that is not loaded reads "another entry".
 */
export function relativeText(
  row: ChronicleRowV1,
  byPlacementId: ReadonlyMap<string, ChronicleRowV1>,
): string | null {
  const self = row.placement.id;
  const title = (id: string) => {
    const other = byPlacementId.get(id);
    return other === undefined ? OTHER_ENTRY : rowTitle(other);
  };
  const parts = row.placement.constraints.flatMap((c) => {
    const after = c.afterId === self;
    if (!after && c.beforeId !== self) return [];
    const other = title(after ? c.beforeId : c.afterId);
    if (c.relation === 'same_time') return [`at the same time as ${other}`];
    if (c.relation === 'during') return [after ? `within ${other}` : `contains ${other}`];
    return [`${lagText(c)}${after ? 'after' : 'before'} ${other}`];
  });
  return parts.length === 0 ? null : parts.join(', ');
}

/** The span a placement's own points cover, `hi` exclusive; null when either end is open. */
function ownRange(placement: PlacementV1): { lo: number; hi: number } | null {
  const lo = (t: TickResultV1 | undefined) =>
    t?.status === 'ok' || t?.status === 'open_hi' ? t.lo : null;
  const hi = (t: TickResultV1 | undefined) =>
    t?.status === 'ok' || t?.status === 'open_lo' ? t.hi : null;
  if (placement.start === null || placement.ongoing) return null;
  const from = lo(placement.start.ticks);
  const to = hi((placement.end ?? placement.start).ticks);
  return from === null || to === null || from >= to ? null : { lo: from, hi: to };
}

const DAY = PRECISIONS.indexOf('day');

/** "Between A and B", one precision finer while both sides read the same ("Spring 1041 AL"). */
function betweenText(calendar: CompiledCalendar, lo: number, hi: number, precision: Precision) {
  for (let i = PRECISIONS.indexOf(precision); i < PRECISIONS.length; i++) {
    const at = PRECISIONS[i] as Precision;
    const a = formatTick(calendar, lo, at);
    const b = formatTick(calendar, hi - 1, at);
    if (a === null || b === null) return null;
    if (a !== b || i === PRECISIONS.length - 1) return `Between ${a} and ${b}`;
  }
  return null;
}

export interface RangeBar {
  readonly text: string;
  readonly left: number | null;
  readonly width: number | null;
}

/**
 * The possible range of a fuzzy, coarse or spanning row: its text, and where it lies in its group
 * (fractions of the group) when it lies inside it; null for an exact point of a day or finer.
 */
export function rangeBar(
  placement: PlacementV1,
  group: Pick<ChronicleGroup, 'lo' | 'hi'>,
  calendar: CompiledCalendar,
): RangeBar | null {
  const start = placement.start;
  if (start === null) return null;
  const { precision, qualifier } = start.point;
  const fuzzy = qualifier !== 'exact' || PRECISIONS.indexOf(precision) < DAY;
  if (!fuzzy && placement.kind !== 'span') return null;
  const range = ownRange(placement);
  if (range === null) return null;
  const text = betweenText(calendar, range.lo, range.hi, precision);
  if (text === null) return null;
  const { lo, hi } = group;
  if (lo === null || hi === null || range.lo < lo || range.hi > hi) {
    return { text, left: null, width: null };
  }
  const size = hi - lo;
  return { text, left: (range.lo - lo) / size, width: (range.hi - range.lo) / size };
}

/** `exact`, `fuzzy` (qualified or coarser than a day), `relative` (undated) for a row's date. */
export function datePrecision(placement: PlacementV1): 'exact' | 'fuzzy' | 'relative' {
  const start = placement.start;
  if (start === null) return 'relative';
  const { precision, qualifier } = start.point;
  return qualifier !== 'exact' || PRECISIONS.indexOf(precision) < DAY ? 'fuzzy' : 'exact';
}

/**
 * Where the NOW divider goes: before the first row whose lower bound is after NOW (a row whose
 * bound equals NOW stays before it), at the end when none is; null while NOW is unset. Rows with
 * no lower bound never decide it.
 */
export function nowDividerIndex(
  rows: readonly unknown[],
  los: readonly (number | null)[],
  now: number | null,
): number | null {
  if (now === null) return null;
  for (let i = 0; i < rows.length; i++) {
    const lo = los[i] ?? null;
    if (lo !== null && lo > now) return i;
  }
  return rows.length;
}

/** A row after NOW, or planned, is dimmed. */
export function isDimmed(row: ChronicleRowV1, index: number, divider: number | null): boolean {
  return (divider !== null && index >= divider) || row.placement.effectiveStatus === 'planned';
}

const STATUS_BADGES: Partial<Record<EffectiveStatus, string>> = {
  planned: 'Planned',
  rumor: 'Rumor',
  alternate: 'Alternate',
};

/** "Planned", "Rumor", "Alternate"; none for canon. */
export function statusBadge(effectiveStatus: EffectiveStatus): string | null {
  return STATUS_BADGES[effectiveStatus] ?? null;
}

export const VIOLATION_TEXT: Readonly<Record<ViolationKind, string>> = {
  cycle: 'Ignored: it closes a loop',
  date_contradiction: 'Contradicts a fixed date',
  invalid_date: "This date can't be placed on the calendar",
  lag_unsatisfiable: 'The gap contradicts the known dates',
  lag_unresolved: "The gap can't be measured yet",
};

/** A row's violations; an overlay one only on a row that is not canon (baseline section 6, rule 2). */
export function rowViolations(row: ChronicleRowV1): ChronViolationV1[] {
  return row.violations.filter((v) => !v.overlay || row.placement.effectiveStatus !== 'canon');
}

export interface BannerEntry {
  readonly violation: ChronViolationV1;
  readonly row: ChronicleRowV1;
}

/** The banner's contradictions: not overlay, not `lag_unresolved`, once per violation (ruling f). */
export function bannerViolations(rows: readonly ChronicleRowV1[]): BannerEntry[] {
  const seen = new Set<string>();
  const entries: BannerEntry[] = [];
  for (const row of rows) {
    for (const violation of row.violations) {
      if (violation.overlay || violation.kind === 'lag_unresolved') continue;
      if (seen.has(violation.id)) continue;
      seen.add(violation.id);
      entries.push({ violation, row });
    }
  }
  return entries;
}

/** The Inbox's chronology category, when the Inbox exists and `catalogue` is on. */
export function inboxLink(routePaths: readonly string[], flags: Flags): string | null {
  return inboxVisible(routePaths, flags) ? '/inbox?category=chrono_violation' : null;
}

/** Every row by placement id, for the relative texts (the list's rows and the tray's). */
export function byPlacement(...lists: readonly (readonly ChronicleRowV1[])[]) {
  const map = new Map<string, ChronicleRowV1>();
  for (const list of lists) for (const row of list) map.set(row.placement.id, row);
  return map;
}
